import React from 'react';
import { 
  Typography, 
  Grid, 
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const PricingPage: React.FC = () => {
  const [billingAnnual, setBillingAnnual] = React.useState(false);
  
  const handleBillingChange = () => {
    setBillingAnnual(!billingAnnual);
  };
  
  const plans = [
    {
      title: 'Free',
      price: 0,
      description: 'Basic temporary email for casual users',
      features: [
        { title: 'Random email address', included: true },
        { title: '24-hour email lifespan', included: true },
        { title: 'Up to 10 MB attachments', included: true },
        { title: '3 replies per email address', included: true },
        { title: 'Real-time email notifications', included: true },
        { title: 'HTML email support', included: true },
        { title: 'Custom email domains', included: false },
        { title: 'Custom email address', included: false },
        { title: 'Longer email retention', included: false },
        { title: 'Unlimited replies', included: false },
        { title: 'Multiple email addresses', included: false },
        { title: 'Priority support', included: false },
      ],
      buttonText: 'Get Started',
      buttonVariant: 'outlined',
      highlight: false
    },
    {
      title: 'Premium',
      price: billingAnnual ? 3.99 : 5.99,
      period: billingAnnual ? '/month (billed annually)' : '/month',
      description: 'Advanced features for power users',
      features: [
        { title: 'Random email address', included: true },
        { title: 'Extended 7-day email lifespan', included: true },
        { title: 'Up to 25 MB attachments', included: true },
        { title: 'Unlimited replies', included: true },
        { title: 'Real-time email notifications', included: true },
        { title: 'HTML email support', included: true },
        { title: '3 custom email domains', included: true },
        { title: 'Custom email address', included: true },
        { title: 'Up to 5 active email addresses', included: true },
        { title: 'Email forwarding', included: true },
        { title: 'No ads', included: true },
        { title: 'Priority support', included: true },
      ],
      buttonText: 'Subscribe Now',
      buttonVariant: 'contained',
      highlight: true
    },
    {
      title: 'Business',
      price: billingAnnual ? 9.99 : 14.99,
      period: billingAnnual ? '/month (billed annually)' : '/month',
      description: 'Team solution for businesses',
      features: [
        { title: 'Random email address', included: true },
        { title: 'Extended 30-day email lifespan', included: true },
        { title: 'Up to 50 MB attachments', included: true },
        { title: 'Unlimited replies', included: true },
        { title: 'Real-time email notifications', included: true },
        { title: 'HTML email support', included: true },
        { title: 'Unlimited custom email domains', included: true },
        { title: 'Custom email address', included: true },
        { title: 'Unlimited active email addresses', included: true },
        { title: 'Email forwarding', included: true },
        { title: 'API access', included: true },
        { title: 'Dedicated support', included: true },
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'outlined',
      highlight: false
    }
  ];

  return (
    <PageTemplate 
      title="Premium Plans" 
      description="Choose the right plan for your privacy needs. From our free basic service to premium features for power users."
      breadcrumbs={[{ label: 'Premium', path: '/pricing' }]}
    >
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          Find the Perfect Plan for You
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={billingAnnual}
                onChange={handleBillingChange}
                color="primary"
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color={billingAnnual ? 'text.secondary' : 'primary.main'} fontWeight={billingAnnual ? 400 : 600}>
                  Monthly
                </Typography>
                <Typography variant="body2" color={billingAnnual ? 'primary.main' : 'text.secondary'} fontWeight={billingAnnual ? 600 : 400}>
                  Annual (Save 20%)
                </Typography>
                {billingAnnual && (
                  <Chip 
                    label="Save 20%" 
                    size="small" 
                    color="primary" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      '& .MuiChip-label': { px: 1 } 
                    }} 
                  />
                )}
              </Stack>
            }
            labelPlacement="end"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: plan.highlight ? 'primary.main' : 'divider',
                position: 'relative',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              {plan.highlight && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 24,
                    transform: 'translateY(-50%)',
                    bgcolor: 'primary.main',
                    color: 'white',
                    py: 1,
                    px: 2.5,
                    borderRadius: 5,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 1,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  MOST POPULAR
                </Box>
              )}
              
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
                  {plan.title}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                  <Typography component="span" variant="h3" fontWeight={700}>
                    ${plan.price}
                  </Typography>
                  {plan.period && (
                    <Typography component="span" variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                      {plan.period}
                    </Typography>
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {plan.description}
                </Typography>
                
                <Button 
                  fullWidth 
                  variant={plan.buttonVariant as 'contained' | 'outlined'} 
                  color="primary"
                  size="large"
                  sx={{ mb: 3 }}
                >
                  {plan.buttonText}
                </Button>
                
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Features include:
                </Typography>
                
                <List disablePadding>
                  {plan.features.map((feature, featureIndex) => (
                    <ListItem 
                      key={featureIndex} 
                      disableGutters 
                      sx={{ 
                        py: 0.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {feature.included ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="disabled" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.title}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: feature.included ? 'textPrimary' : 'text.secondary',
                          style: { textDecoration: feature.included ? 'none' : 'line-through' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Divider sx={{ my: 6 }} />
      
      <Box>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
          Frequently Asked Questions
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              What happens when my premium subscription ends?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              When your premium subscription expires, your account will revert to the free plan features. Any active email addresses beyond the free plan limit will be deactivated, but you'll have 7 days to download any emails before they're removed.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Can I cancel my subscription at any time?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Yes, you can cancel your subscription at any time. If you cancel, you'll still have access to Premium features until the end of your current billing period. We don't offer prorated refunds for unused time.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              How do custom domains work?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              With Premium and Business plans, you can choose from our selection of pre-approved domains or request to use your own domain. This allows you to create email addresses that look more professional or fit specific needs.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Is there a money-back guarantee?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Yes, we offer a 14-day money-back guarantee for Premium and Business subscriptions. If you're not satisfied with our service for any reason, contact our support team within 14 days of your purchase for a full refund.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PageTemplate>
  );
};

export default PricingPage; 